import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { HenviserDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/henviser-dto';
import { VirksomhedDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/virksomhed-dto';
import { HenviserRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/henviser-request';
import { HenviserService, VirksomhedService } from 'src/app/api/services';
import { HenviserDialogData } from 'src/app/components/aftale/models';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { StamdataStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/stamdata-status-enum';
import { StamdataStatusService } from '../../shared/stamdata-status.service';
import { StamdatastatusModel } from '../../shared/stamdatastatus.model';
import { NotificationService } from 'src/app/notifications/services/notification.service';

@Component({
    selector: 'app-henviser-dialog',
    templateUrl: './henviser-dialog.component.html',
    styleUrls: ['./henviser-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HenviserDialogComponent implements OnInit {
  henviser: HenviserDto;
  virksomhed: VirksomhedDto;
  form: UntypedFormGroup;
  unsubscribe$ = new Subject<void>();
  stamdatastatusvalues: Array<StamdatastatusModel> = [];

  get cvr() {
    return this.form?.get('cvr');
  }

  get name() {
    return this.form?.get('name');
  }

  get stamdatastatus() {
    return this.form?.get('stamdatastatus');
  }

  constructor(
    private virksomhedService: VirksomhedService,
    private henviserService: HenviserService,
    private formBuilder: UntypedFormBuilder,
    private errorHandler: ErrorHandlerService,
    private notifications: NotificationService,
    public dialogRef: MatDialogRef<HenviserDialogComponent>,
    private stamdatastatusservice: StamdataStatusService,
    @Inject(MAT_DIALOG_DATA) public data: HenviserDialogData
  ) {
    this.henviser = data.henviser;
    this.stamdatastatusvalues = [StamdataStatusEnum.Yes, StamdataStatusEnum.No].map((p) => {
      return { value: p, name: this.stamdatastatusservice.stamdatastatus(p) };
    });
    this.form = this.formBuilder.group({
      cvr: ['', [Validators.required, Validators.minLength(1)]],
      name: ['', [Validators.required, Validators.minLength(3)]],
      stamdatastatus: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (this.data.id) {
      this.cvr.setValue(this.data.cvr);
      this.name.setValue(this.data.name);
      this.stamdatastatus.setValue(this.data.henviser.henviserStatus);
    }
    if (this.data.searchQuery) {
      this.virksomhedService
        .searchVirksomhed({
          caller: EGIS_SERVICE_CALLER,
          searchQuery: this.data.searchQuery,
        })
        .pipe()
        .subscribe(
          (response: VirksomhedDto) => {
            this.virksomhed = response;
            if (response) {
              this.cvr.setValue(response.cvr);
              this.name.setValue(response.name);
              this.stamdatastatus.setValue(StamdataStatusEnum.Yes);
            } else {
              this.cvr.setValue(this.data.searchQuery);
              this.name.setValue(this.data.searchQuery);
            }
          },
          (error) => {
            this.errorHandler.HandleError(error);
          }
        );
    }
  }

  add() {
    const request: HenviserRequest = {
      cvr: this.cvr.value,
      name: this.name.value,
      henviserStatus: this.stamdatastatus.value,
    };
    this.henviserService
      .addHenviser({
        caller: EGIS_SERVICE_CALLER,
        body: request,
      })
      .pipe()
      .subscribe(
        (r) => {
          this.notifications.success(`Henviser med cvr: ${r.cvr} er oprettet`);
          this.dialogRef.close(r);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  cancel() {
    this.dialogRef.close();
  }

  update() {
    this.henviserService
      .updateHenviser({
        caller: EGIS_SERVICE_CALLER,
        body: {
          cvr: this.cvr.value,
          name: this.name.value,
          henviserStatus: this.stamdatastatus.value,
          id: this.henviser.id,
        },
      })
      .pipe()
      .subscribe(
        (r) => {
          this.notifications.success(`Henviser med cvr: ${r.cvr} er opdateret`);

          this.dialogRef.close(r);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  searchByCvr() {
    if (this.cvr.value)
      this.virksomhedService.searchVirksomhed({ searchQuery: this.cvr.value, caller: EGIS_SERVICE_CALLER }).subscribe((p) => {
        if (p) {
          this.name.setValue(p.name);
          this.cvr.setValue(p.cvr);
          this.stamdatastatus.setValue(StamdataStatusEnum.Yes);
        } else {
          this.notifications.warning(`Virksomhed med cvr: ${this.cvr.value} kan ikke findes`);
        }
      });
  }

  onCvrKeyDownEvent(event: KeyboardEvent) {
    if (event.key == 'Enter' && this.cvr.valid && !this.name.value) this.searchByCvr();
  }

  EnumToArray(enumme) {
    return Object.keys(enumme).map((key) => enumme[key]);
  }
}
